import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { PlanLimitError } from '../../../features/plans/models/enforcement.model';
import { PlanLimitErrorService } from '../../../features/plans/services/plan-limit-error.service';
import { PlansSharedModule } from '../../../features/plans/plans-shared.module';
import { AuthService } from '../../../services/auth/auth.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { LanguageService } from '../../../services/language/language.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { UserService } from '../../../services/user.service';
import { AuthWizardTimezoneCatalog, WizardTimezone } from '../auth-wizard-timezone.catalog';
import { InviteFlowInitializer } from './invite-flow.initializer';
import { InviteOnboardingSubmitter } from './invite-onboarding.submitter';
import { InviteOnboardingTokenVerifier } from './invite-onboarding-token.verifier';
import { InviteWizardCredentialsAndCompletionComponent } from './invite-wizard-credentials-and-completion.component';
import { InviteWizardPreferenceCoordinator, InviteWizardLanguage } from './invite-wizard-preference.coordinator';
import { InviteWizardProfileStepsComponent } from './invite-wizard-profile-steps.component';

export interface ValidateTokenResponse {
  id: string;
  email: string;
  status: string;
  expiresAt: string;
  roleName: string;
  companyName: string;
}

@Component({
  selector: 'app-invite-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    PlansSharedModule,
    InviteWizardCredentialsAndCompletionComponent,
    InviteWizardProfileStepsComponent,
  ],
  templateUrl: './invite-wizard.component.html',
  styleUrls: [
    '../wizard/wizard.component.layout-and-progress.scss',
    '../wizard/wizard.component.preferences-and-responsive.scss',
  ],
})
export class InviteWizardComponent implements OnInit {
  currentStep = 0;
  wizardForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  isValidating = true;
  validationError = '';
  planLimitError: PlanLimitError | null = null;
  invite: ValidateTokenResponse | null = null;
  token = '';
  email = '';
  timezones: WizardTimezone[] = [];
  timezoneQuery = '';

  readonly steps = [
    { title: 'wizard.welcome_title', description: 'wizard.welcome_description' },
    { title: 'wizard.preferences_title', description: 'wizard.preferences_description' },
    { title: 'wizard.profile_title', description: 'wizard.profile_description' },
    { title: 'invite.password_step', description: 'invite.password_step_desc' },
    { title: 'wizard.complete_title', description: 'wizard.complete_description' },
  ];

  private allTimezones: WizardTimezone[] = [];
  private readonly timezoneCatalog = new AuthWizardTimezoneCatalog();
  private readonly preferenceCoordinator: InviteWizardPreferenceCoordinator;
  private readonly tokenVerifier: InviteOnboardingTokenVerifier;
  private readonly flowInitializer: InviteFlowInitializer;
  private readonly onboardingSubmitter: InviteOnboardingSubmitter;

  constructor(
    route: ActivatedRoute,
    formBuilder: FormBuilder,
    authService: AuthService,
    auth0: Auth0Service,
    userService: UserService,
    public router: Router,
    public themeService: ThemeService,
    public languageService: LanguageService,
    planLimitErrorService: PlanLimitErrorService,
    frontendLogger: FrontendLoggerService
  ) {
    this.preferenceCoordinator = new InviteWizardPreferenceCoordinator(
      themeService,
      languageService
    );
    this.tokenVerifier = new InviteOnboardingTokenVerifier(auth0, frontendLogger);
    this.flowInitializer = new InviteFlowInitializer(
      route,
      router,
      formBuilder,
      authService,
      userService,
      themeService,
      languageService,
      frontendLogger,
      this.timezoneCatalog
    );
    this.onboardingSubmitter = new InviteOnboardingSubmitter(
      authService,
      auth0,
      userService,
      router,
      planLimitErrorService,
      frontendLogger,
      this.tokenVerifier
    );
  }

  ngOnInit(): void {
    void this.initializeInviteFlow();
  }

  initializeInviteFlow(): Promise<void> {
    return this.flowInitializer.initialize({
      setInviteContext: (token, email) => {
        this.token = token;
        this.email = email;
      },
      reject: (errorKey) => {
        this.validationError = errorKey;
        this.isValidating = false;
      },
      accept: (invite, form, timezones) => {
        this.invite = invite;
        this.wizardForm = form;
        this.timezones = timezones;
        this.allTimezones = [...timezones];
        this.isValidating = false;
      },
    });
  }

  nextStep(): void {
    this.currentStep = this.preferenceCoordinator.nextStep(
      this.currentStep,
      this.steps.length,
      this.wizardForm
    );
  }

  previousStep(): void {
    this.currentStep = this.preferenceCoordinator.previousStep(this.currentStep);
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.preferenceCoordinator.setTheme(this.wizardForm, theme);
  }

  setLanguage(language: InviteWizardLanguage): void {
    this.preferenceCoordinator.setLanguage(this.wizardForm, language);
  }

  markFormGroupTouched(form: FormGroup): void {
    this.preferenceCoordinator.markFormGroupTouched(form);
  }

  translate(key: string): string {
    return this.preferenceCoordinator.translate(key);
  }

  onTimezoneQueryChange(value: string): void {
    this.timezoneQuery = value;
    this.timezones = this.preferenceCoordinator.filterTimezones(
      this.wizardForm,
      this.allTimezones,
      value
    );
  }

  onTimezoneEnter(): void {
    this.preferenceCoordinator.selectOnlyTimezone(this.wizardForm, this.timezones);
  }

  completeWizard(): Promise<void> {
    return this.onboardingSubmitter.submit({
      invite: this.invite,
      token: this.token,
      email: this.email,
      form: this.wizardForm,
    }, {
      setLoading: (value) => { this.isLoading = value; },
      setError: (message) => { this.errorMessage = message; },
      setPlanLimit: (error) => { this.planLimitError = error; },
      setStep: (step) => { this.currentStep = step; },
      touchForm: (form) => this.markFormGroupTouched(form),
      translate: (key) => this.translate(key),
    });
  }

}
